// Drive locomotion from playback or scrub displacement; reset at stationary holds and branch replacements.

// A player carries motion when their OWN keys move them (two or more) or when
// they are MARKING / FOLLOWING someone else — the attach lane is what carries
// them then, and their own key count says nothing about whether they are
// running. The old key-count-only gate is why a marked player slid across the
// pitch standing still: the displacement was real, the gate never saw it.
const carriesMotion = (track) => (track?.position?.length >= 2) || (track?.attach?.length >= 1);

// A world-class sprint is around 10 m/s. Anything faster than this in a single
// frame is not a run — it is a PLACEMENT: a mark key putting a defender next to
// their marker, a branch swap, a drag. The seek case is already caught by the
// time gap, but a mark transition advances the clock normally while the
// position jumps several metres, and feeding that to the gait left a player
// sprinting at 20 m/s for a third of a second every time they picked up a mark.
const MAX_RUN_SPEED = 25;

export function playerTravelSpeed(data, track, time, position, delta, playing = true) {
    const previous = data.travelSample;
    if (previous?.track === track && time === previous.time && carriesMotion(track) &&
        (playing || (previous.scrubbing && !previous.playing && (previous.elapsed || 0) + (delta || 0.016) < 0.1))) {
        previous.elapsed = (previous.elapsed || 0) + (delta || 0.016);
        return data.smoothSpeed || 0;
    }
    data.travelSample = { track, time, position: position.slice(), elapsed: 0, playing, scrubbing: false };
    let speed = 0;
    data.travelDirection = null;
    // Across a branch hop the track OBJECT changes even though it is still the same
    // player, so object identity alone zeroes their motion at every boundary — which
    // is why a master-timeline scrub makes players stop and start again. While
    // SCRUBBING, accept a different track object while the clock is moving.
    // A scrub can cover arbitrary ground in one render frame. During playback
    // the strict identity gate stays: a branch swap is a new context.
    const samePlayerContext = previous && (previous.track === track || !playing);
    if (carriesMotion(track) && samePlayerContext &&
        (playing ? time > previous.time && time - previous.time < 0.5 : time !== previous.time)) {
        const dx = position[0] - previous.position[0], dz = position[2] - previous.position[2];
        const step = Math.hypot(dx, dz);
        const frame = Math.max((delta || 0.016) + (previous.elapsed || 0), 0.0001);
        // During playback, reject impossible jumps. During scrubbing, visible
        // displacement always drives a gait, even when time moves very quickly.
        if (!playing || step / frame <= MAX_RUN_SPEED) {
            data.travelDirection = { dx, dz };
            data.travelSample.scrubbing = !playing && step > 0.000001;
            // Scrubbing deliberately compresses timeline travel into one frame.
            // Cap the gait speed instead of discarding that visible movement.
            speed = Math.min(MAX_RUN_SPEED, step / frame);
        }
    }
    // A stationary sample must not inherit a running EMA from the last branch.
    data.smoothSpeed = speed > 0.000001
        ? (!playing ? speed : (data.smoothSpeed || 0) + (speed - (data.smoothSpeed || 0)) * Math.min(1, (delta || 0.016) / 0.3))
        : 0;
    return data.smoothSpeed;
}

// Forward is local +Z, matching playerFacingYaw's atan2(dx, dz).
export function gaitPlaybackDirection(data, yaw) {
    const motion = data.travelDirection;
    if (!motion || !data.smoothSpeed) { data.gaitDirection = 1; return 1; }
    const length = Math.hypot(motion.dx, motion.dz);
    if (length < 0.000001) return 1;
    const alignment = (motion.dx * Math.sin(yaw) + motion.dz * Math.cos(yaw)) / length;
    // A small neutral band prevents sideways motion from flipping every frame.
    if (alignment < -0.1) data.gaitDirection = -1;
    else if (alignment > 0.1) data.gaitDirection = 1;
    return data.gaitDirection || 1;
}
